/**
 * Responsive Chart Utilities
 * Provides mobile-responsive font sizing for all chart components
 *
 * Mobile Breakpoints:
 * - Mobile: < 640px (sm)
 * - Tablet: 640px - 1024px (sm to lg)
 * - Desktop: >= 1024px (lg+)
 */

/**
 * Get responsive font sizes based on current screen width
 * Returns appropriate font sizes for chart elements
 */
export function getResponsiveFontSizes() {
  // Server-side rendering safety
  if (typeof window === 'undefined') {
    return {
      title: 32,
      subtitle: 20,
      dataDescription: 16,
      legend: 16,
      cta: 20,
    };
  }

  const isMobile = window.innerWidth < 640;
  const isTablet = window.innerWidth >= 640 && window.innerWidth < 1024;

  return {
    title: isMobile ? 18 : isTablet ? 24 : 32,
    subtitle: isMobile ? 14 : isTablet ? 16 : 20,
    dataDescription: isMobile ? 12 : isTablet ? 14 : 16,
    legend: isMobile ? 12 : isTablet ? 14 : 16,
    cta: isMobile ? 14 : isTablet ? 16 : 20,
  };
}

/**
 * Chart types for data-density-aware height scaling
 */
export type ChartDensityType = 'stage' | 'category' | 'transaction' | 'comparison' | 'default';

/**
 * Get responsive chart height based on screen width and data density
 *
 * Chart-specific mobile multipliers based on actual data structure analysis:
 * - stage: 71% (9 stacked stages + 9 legend items)
 * - category: 71% (dual-axis + 4 series with multiple label sets)
 * - transaction: 82% (12 stacked ranges + 12 legend items - MOST DENSE)
 * - comparison: 100% (5-10 country stacks + wrapping legend - NO REDUCTION)
 * - default: 60% (simple charts with <5 legend items)
 *
 * @param baseHeight - Desktop height in pixels
 * @param chartType - Chart density type (optional, defaults to 'default')
 * @returns Responsive height in pixels
 */
export function getResponsiveChartHeight(
  baseHeight: number,
  chartType: ChartDensityType = 'default'
): number {
  if (typeof window === 'undefined') return baseHeight;

  const isMobile = window.innerWidth < 640;
  const isTablet = window.innerWidth >= 640 && window.innerWidth < 1024;

  // Tablet: Always 80% for all charts
  if (isTablet) return Math.round(baseHeight * 0.8);

  // Mobile: Chart-specific multipliers based on data density
  if (isMobile) {
    const mobileMultipliers: Record<ChartDensityType, number> = {
      stage: 0.71,        // 600/850 - 9 legend items wrapping to 3 lines
      category: 0.71,     // 500/700 - dual-axis label density
      transaction: 0.82,  // 700/850 - 12 legend items wrapping to 4 lines
      comparison: 1.0,    // 500/500 - 10 countries, NO reduction (touch targets)
      default: 0.6        // Simple charts, 60% reduction OK
    };
    return Math.round(baseHeight * mobileMultipliers[chartType]);
  }

  // Desktop: 100% of base height
  return baseHeight;
}

/**
 * Check if current viewport is mobile
 */
export function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 640;
}

/**
 * Check if current viewport is tablet
 */
export function isTabletViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 640 && window.innerWidth < 1024;
}

/**
 * Check if current viewport is desktop
 */
export function isDesktopViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 1024;
}
